// Exams API client (double marking). Contract: build doc 01 §5–§6; function
// list: build doc 05 §3.1.
//
// Failure convention: every call either resolves the parsed body or THROWS an
// ExamsApiError {status, code, message, details}. Nothing resolves null or
// undefined on failure, nothing toasts, and a 404 never redirects. A 401 runs
// the same session-expiry branch as service.js (LMS frame → relaunch notice;
// Teacher Loop flag → keep its keys; else clear the session and go to '/'),
// then rejects with code 'UNAUTHENTICATED'.
//
// Transport rules (90 #1–#3): authenticated paths have NO /api prefix; the
// only headers ever sent are Authorization, Content-Type and Idempotency-Key
// (CORS allows nothing else: no If-Match, no token header). Versions travel in
// the body as `expectedVersion`.
import { v4 as uuidv4 } from 'uuid';
import { observeJourneyOrder, startJourneyRequest } from '../journeyInstrumentation';
import { deleteProfileCookies, getAuthToken } from '../userLocalDetails';
import {
  isEmbeddedMode,
  notifyEmbeddedSessionExpired,
} from '../utils/ltiEmbedded';
import { endSessionKeepingTeacherLoop } from '../utils/teacherLoopSession';

export const examsApiBase = () =>
  (process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8080').replace(
    /\/+$/,
    ''
  );

export class ExamsApiError extends Error {
  constructor(
    message,
    { status = 0, code = null, details = {}, body = null } = {}
  ) {
    super(message);
    this.name = 'ExamsApiError';
    this.status = status;
    this.code = code;
    this.details = details || {};
    this.body = body;
  }
}

/** A fresh Idempotency-Key (UUID v4): one per user action (01 K33). */
export const newIdempotencyKey = () => uuidv4();

export const enc = (value) => encodeURIComponent(String(value ?? ''));

/** `?a=1&b=2` from the defined, non-null entries ('' when none). */
export const queryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.append(key, Array.isArray(value) ? value.join(',') : String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

export const parseBody = async (response) => {
  try {
    const text = await response.text();
    if (!text || !text.trim()) return null;
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/** service.js's 401 branch (fetchData / modifyData), without the toast. */
export const endExpiredSession = () => {
  if (isEmbeddedMode()) {
    notifyEmbeddedSessionExpired();
    return;
  }
  if (process.env.REACT_APP_TEACHER_LOOP === 'on') {
    if (!endSessionKeepingTeacherLoop(deleteProfileCookies)) {
      window.location.href = '/';
    }
    return;
  }
  deleteProfileCookies();
  localStorage.clear();
  window.location.href = '/';
};

/** The error for a non-2xx reply whose body is 01's {code, message, details}. */
export const errorFromResponse = (response, data) =>
  new ExamsApiError(data?.message || `Request failed (${response.status})`, {
    status: response.status,
    code: data?.code || null,
    details: data?.details || {},
    body: data,
  });

/**
 * One authenticated Exams request.
 * @param {string} path  API path starting with "/" (no /api prefix).
 * @param {object} [opts]
 * @param {string} [opts.method]
 * @param {object} [opts.body]           sent as JSON
 * @param {string} [opts.idempotencyKey] sent as the Idempotency-Key header
 * @param {AbortSignal} [opts.signal]
 * @param {boolean} [opts.raw]           resolve the Response itself (after the ok check)
 * @returns parsed JSON; an empty 2xx body resolves {ok: true, status}
 */
export const examsRequest = async (
  path,
  { method = 'GET', body, idempotencyKey, signal, raw = false } = {}
) => {
  const finishJourney = startJourneyRequest(path, method);
  const token = getAuthToken();
  let response;
  try {
    response = await fetch(`${examsApiBase()}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(idempotencyKey && { 'Idempotency-Key': idempotencyKey }),
      },
      ...(body !== undefined && { body: JSON.stringify(body) }),
      ...(signal && { signal }),
    });
  } catch (error) {
    finishJourney('failure');
    if (error?.name === 'AbortError') {
      throw new ExamsApiError('Request cancelled', { code: 'ABORTED' });
    }
    throw new ExamsApiError(error?.message || 'Network error', {
      code: 'NETWORK',
    });
  }
  if (response.status === 401) {
    finishJourney('failure', response.status);
    endExpiredSession();
    throw new ExamsApiError('Your session has expired.', {
      status: 401,
      code: 'UNAUTHENTICATED',
    });
  }
  if (!response.ok) {
    finishJourney('failure', response.status);
    throw errorFromResponse(response, await parseBody(response));
  }
  if (raw) {
    finishJourney('accepted', response.status);
    return response;
  }
  const data = await parseBody(response);
  if (method === 'GET') observeJourneyOrder(path, data);
  finishJourney(
    data === null ? 'unconfirmed' : response.status === 202 ? 'accepted' : 'success',
    response.status
  );
  return data === null ? { ok: true, status: response.status } : data;
};

const get = (path, opts) => examsRequest(path, opts);
const post = (path, body = {}, opts = {}) =>
  examsRequest(path, { ...opts, method: 'POST', body });
const put = (path, body = {}, opts = {}) =>
  examsRequest(path, { ...opts, method: 'PUT', body });
const patch = (path, body = {}, opts = {}) =>
  examsRequest(path, { ...opts, method: 'PATCH', body });
const del = (path, opts = {}) =>
  examsRequest(path, { ...opts, method: 'DELETE' });

const exam = (examId) => `/exams/${enc(examId)}`;
const job = (jobId) => `/ingestion/${enc(jobId)}`;
const keyOr = (key) => key || newIdempotencyKey();

const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

/**
 * PUT a file to a signed GCS URL, echoing the server's requiredHeaders. No
 * Authorization: the URL is its own credential.
 */
export const putToSignedUrl = async (
  uploadUrl,
  file,
  requiredHeaders = {},
  { signal } = {}
) => {
  let response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { ...requiredHeaders },
      body: file,
      ...(signal && { signal }),
    });
  } catch (error) {
    throw new ExamsApiError(error?.message || 'Upload failed', {
      code: error?.name === 'AbortError' ? 'ABORTED' : 'NETWORK',
    });
  }
  if (!response.ok) {
    throw new ExamsApiError(`Upload failed (${response.status})`, {
      status: response.status,
      code: 'UPLOAD_FAILED',
    });
  }
  return { ok: true, status: response.status };
};

// ------------------------------------------------------------ exams (01 §5.1)

/** GET /exams → {items, nextCursor}. range ALL|LAST_30_DAYS|LAST_90_DAYS|THIS_YEAR. */
export const listExams = ({
  range = 'ALL',
  tz = browserTimeZone(),
  cursor,
  limit = 50,
  signal,
} = {}) =>
  get(`/exams${queryString({ range, tz, cursor, limit })}`, { signal });

/** POST /exams → ExamView (also creates the exam's ingestion job, 90 #37). */
export const createExam = (body = {}, idempotencyKey) =>
  post('/exams', body, { idempotencyKey });

export const getExam = (examId, { signal } = {}) =>
  get(exam(examId), { signal });

/** PATCH /exams/{id} body {expectedVersion, fields} (90 #2). */
export const patchExam = (examId, expectedVersion, fields) =>
  patch(exam(examId), { expectedVersion, fields });

export const deleteExam = (examId) => del(exam(examId));

export const duplicateExam = (examId, idempotencyKey) =>
  post(
    `${exam(examId)}/duplicate`,
    {},
    { idempotencyKey: keyOr(idempotencyKey) }
  );

/** POST /exams/{id}/steps/{STEP}; step is the wire ExamStep (UPPER_SNAKE). */
export const goToStep = (examId, step) =>
  post(`${exam(examId)}/steps/${enc(step)}`);

export const markDone = (examId, { acknowledgeOpenRemarks = false } = {}) =>
  post(`${exam(examId)}/done`, { acknowledgeOpenRemarks });

// ---------------------------------------------------- status, notices (§5.4)

export const getExamStatus = (examId, { signal } = {}) =>
  get(`${exam(examId)}/status`, { signal });

export const getNotices = () => get('/exams/notices');

export const readNotice = (noticeId) =>
  post(`/exams/notices/${enc(noticeId)}/read`);

// ------------------------------------------------------------ assets (§5.2)

/** POST /exams/{id}/assets: a file ({purpose, fileName, contentType, sizeBytes}) or text ({purpose, text, name}). */
export const createExamAsset = (examId, body) =>
  post(`${exam(examId)}/assets`, body);

export const completeExamAsset = (examId, assetId) =>
  post(`${exam(examId)}/assets/${enc(assetId)}/complete`);

export const deleteExamAsset = (examId, assetId) =>
  del(`${exam(examId)}/assets/${enc(assetId)}`);

/** POST /exams/{id}/asset-urls {assetIds} → {urls: {assetId: {url, expiresAt}}}. */
export const getAssetUrls = (examId, assetIds) =>
  post(`${exam(examId)}/asset-urls`, { assetIds });

/** Sign → PUT the file to the signed URL → complete. Resolves the completed asset. */
export const uploadExamAsset = async (
  examId,
  file,
  purpose,
  { signal } = {}
) => {
  const signed = await createExamAsset(examId, {
    purpose,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
  });
  if (signed.uploadUrl) {
    await putToSignedUrl(signed.uploadUrl, file, signed.requiredHeaders, {
      signal,
    });
  }
  return completeExamAsset(examId, signed.assetId);
};

// ---------------------------------- paper, tools, imports, extras (§5.3)

/** POST /exams/{id}/paper {assetId} → 202 {runId} (A1). */
export const importPaper = (examId, assetId, idempotencyKey) =>
  post(
    `${exam(examId)}/paper`,
    { assetId },
    { idempotencyKey: keyOr(idempotencyKey) }
  );

/** DELETE /exams/{id}/paper → ExamView with the pre-import values. */
export const removePaperImport = (examId) => del(`${exam(examId)}/paper`);

/** GET /exams/{id}/tool-library?kind=&questionId= */
export const getToolLibrary = (examId, { kind, questionId } = {}) =>
  get(`${exam(examId)}/tool-library${queryString({ kind, questionId })}`);

export const getToolLibraryItem = (examId, kind, itemId, questionId) =>
  get(
    `${exam(examId)}/tool-library/${enc(kind)}/${enc(itemId)}${queryString({
      questionId,
    })}`
  );

/** PUT …/questions/{qid}/tools/{RUBRIC|GUIDE} {mode: ATTACH, itemId} | {mode: SAVE_AS_NEW, name, snapshot}. */
export const setQuestionTool = (examId, questionId, kind, body) =>
  put(`${exam(examId)}/questions/${enc(questionId)}/tools/${enc(kind)}`, body);

export const rescaleTools = (examId, questionId) =>
  post(`${exam(examId)}/questions/${enc(questionId)}/tools/rescale`);

export const setFeedbackTools = (examId, questionId, body) =>
  put(`${exam(examId)}/questions/${enc(questionId)}/feedback-tools`, body);

/** POST /exams/{id}/library/{COMMENT_BANK|SUCCESS_CRITERIA} → {itemId, name}. */
export const saveLibraryTool = (examId, kind, body) =>
  post(`${exam(examId)}/library/${enc(kind)}`, body);

/** POST /exams/{id}/imports {kind, assetId, questionId} → 202 {runId}. */
export const startImport = (
  examId,
  { kind, assetId, questionId = null },
  idempotencyKey
) =>
  post(
    `${exam(examId)}/imports`,
    { kind, assetId, questionId },
    { idempotencyKey: keyOr(idempotencyKey) }
  );

export const getImport = (examId, runId) =>
  get(`${exam(examId)}/imports/${enc(runId)}`);

export const addExemplar = (examId, body) =>
  post(`${exam(examId)}/exemplars`, body);

export const updateExemplar = (examId, exemplarId, body) =>
  patch(`${exam(examId)}/exemplars/${enc(exemplarId)}`, body);

export const deleteExemplar = (examId, exemplarId) =>
  del(`${exam(examId)}/exemplars/${enc(exemplarId)}`);

export const addMaterial = (examId, body) =>
  post(`${exam(examId)}/materials`, body);

export const deleteMaterial = (examId, materialId) =>
  del(`${exam(examId)}/materials/${enc(materialId)}`);

// ----------------------------------------------- ingestion (§5.5, §6)

export const getWorkspace = (jobId, { signal } = {}) =>
  get(job(jobId), { signal });

/** PATCH /ingestion/{jobId} {annotated}. */
export const patchIngestion = (jobId, { annotated }) =>
  patch(job(jobId), { annotated });

/** POST /ingestion/{jobId}/files {fileName, contentType, sizeBytes} → {fileId, uploadUrl, requiredHeaders, expiresAt}. */
export const signIngestionFile = (
  jobId,
  { fileName, contentType, sizeBytes }
) => post(`${job(jobId)}/files`, { fileName, contentType, sizeBytes });

export const completeIngestionFile = (jobId, fileId) =>
  post(`${job(jobId)}/files/${enc(fileId)}/complete`);

export const removeIngestionFile = (jobId, fileId) =>
  del(`${job(jobId)}/files/${enc(fileId)}`);

/** POST /ingestion/{jobId}/typed {who, questionId, text, rosterId}. */
export const addTypedResponse = (
  jobId,
  { who, questionId = null, text, rosterId = null }
) => post(`${job(jobId)}/typed`, { who, questionId, text, rosterId });

export const deleteTypedResponse = (jobId, pageId) =>
  del(`${job(jobId)}/typed/${enc(pageId)}`);

/** POST /ingestion/{jobId}/start {annotated} → 202; 402 CREDITS_REQUIRED (details = PayModel PRE). */
export const startIngestion = (jobId, { annotated }, idempotencyKey) =>
  post(
    `${job(jobId)}/start`,
    { annotated },
    { idempotencyKey: keyOr(idempotencyKey) }
  );

/** POST /ingestion/{jobId}/page-urls {pageIds ≤ 200, size THUMB|PREVIEW}. */
export const getPageUrls = (jobId, pageIds, size = 'THUMB') =>
  post(`${job(jobId)}/page-urls`, { pageIds, size });

export const getScriptText = (jobId, scriptId) =>
  get(`${job(jobId)}/scripts/${enc(scriptId)}/text`);

export const getPage = (jobId, pageId) =>
  get(`${job(jobId)}/pages/${enc(pageId)}`);

export const retryPage = (jobId, pageId) =>
  post(`${job(jobId)}/pages/${enc(pageId)}/retry`);

/** POST /ingestion/{jobId}/commands {expectedVersion, label, commands}: one user action per request. */
export const sendCommands = (jobId, expectedVersion, commands, label) =>
  post(`${job(jobId)}/commands`, {
    expectedVersion,
    ...(label ? { label } : {}),
    commands,
  });

/** POST /ingestion/{jobId}/undo {expectedVersion}: pops the server's top group. */
export const undo = (jobId, expectedVersion) =>
  post(`${job(jobId)}/undo`, { expectedVersion });

export const getRoster = (jobId) => get(`${job(jobId)}/roster`);

/** POST /ingestion/{jobId}/roster {assetId} | {entries}. */
export const addRoster = (jobId, payload) =>
  post(`${job(jobId)}/roster`, payload);

export const refreshRoster = (jobId) => post(`${job(jobId)}/roster/refresh`);

/** POST /ingestion/{jobId}/commit {expectedVersion, acknowledge}. */
export const commitIngestion = (
  jobId,
  expectedVersion,
  acknowledge = null,
  idempotencyKey
) =>
  post(
    `${job(jobId)}/commit`,
    { expectedVersion, acknowledge },
    { idempotencyKey: keyOr(idempotencyKey) }
  );

/** POST /ingestion/{jobId}/files/register {uploadedFileId} (BULK only, 90 #38) → 201 file view. */
export const registerIngestionFile = (jobId, uploadedFileId) =>
  post(`${job(jobId)}/files/register`, { uploadedFileId });

/** POST /ingestion/{jobId}/reopen {expectedVersion} (BULK only, 06 §4.3) → job view; 409 DRAFT_ALREADY_SUBMITTED. */
export const reopenIngestion = (jobId, expectedVersion) =>
  post(`${job(jobId)}/reopen`, { expectedVersion });

// ------------------------------------------------ your marks (§5.6)

export const addMarker = (examId, { markerId, name }) =>
  post(`${exam(examId)}/markers`, { markerId, name });

export const renameMarker = (examId, markerId, name) =>
  patch(`${exam(examId)}/markers/${enc(markerId)}`, { name });

export const removeMarker = (examId, markerId) =>
  del(`${exam(examId)}/markers/${enc(markerId)}`);

/** GET /exams/{id}/marks?markerId= → MarksGridView. */
export const getMarks = (examId, markerId = 'owner') =>
  get(`${exam(examId)}/marks${queryString({ markerId })}`);

/**
 * PUT /exams/{id}/marks {markerId, source, entries: [{responseId, mark, rubric}]}
 * (01 §5.6). source is TYPED (grid) or DRAWER; mark null clears a cell;
 * rubric (rubric questions only) is [{criterionId, levelId}].
 */
export const putMarks = (examId, { markerId, source = 'TYPED', entries }) =>
  put(`${exam(examId)}/marks`, {
    markerId,
    source,
    entries: (entries || []).map(({ responseId, mark, rubric = null }) => ({
      responseId,
      mark,
      rubric,
    })),
  });

export const startMarksImport = (
  examId,
  { assetId, markerId, contains },
  idempotencyKey
) =>
  post(
    `${exam(examId)}/marks/imports`,
    { assetId, markerId, contains },
    { idempotencyKey: keyOr(idempotencyKey) }
  );

export const confirmMarksImport = (examId, runId, body = {}) =>
  post(`${exam(examId)}/marks/imports/${enc(runId)}/confirm`, body);

/** GET /exams/{id}/marks/template?contains=MARKS_ONLY|MARKS_AND_GRADES (JSON; CSV built client-side). */
export const getMarksTemplate = (examId, contains = 'MARKS_ONLY') =>
  get(`${exam(examId)}/marks/template${queryString({ contains })}`);

export const assignAnnotationMarks = (examId, { markerId }) =>
  post(`${exam(examId)}/annotation-marks/assign`, { markerId });

export const completeYourMarks = (
  examId,
  { acknowledgeWarnings = false } = {}
) => post(`${exam(examId)}/your-marks/complete`, { acknowledgeWarnings });

export const switchToMarkForMe = (examId) =>
  post(`${exam(examId)}/switch-to-mark-for-me`);

// ----------------------------- results, drawer, re-marks, finals (§5.7)

export const getResults = (examId, { signal } = {}) =>
  get(`${exam(examId)}/results`, { signal });

export const getScript = (examId, scriptId) =>
  get(`${exam(examId)}/scripts/${enc(scriptId)}`);

export const getScriptHistory = (examId, scriptId, { cursor, limit } = {}) =>
  get(
    `${exam(examId)}/scripts/${enc(scriptId)}/history${queryString({
      cursor,
      limit,
    })}`
  );

/** GET /exams/{id}/responses/{rid}/blind: re-marks and samples (no names, marks or history). */
export const getBlindResponse = (examId, responseId) =>
  get(`${exam(examId)}/responses/${enc(responseId)}/blind`);

export const getRemarks = (examId) => get(`${exam(examId)}/remarks`);

/**
 * POST …/responses/{rid}/remark {mark, rubric, guideRowId, outsideDecision, reason}
 * (01 §5.7). Guides send guideRowId with that row's mark.
 */
export const submitRemark = (
  examId,
  responseId,
  {
    mark,
    rubric = null,
    guideRowId = null,
    outsideDecision = null,
    reason = null,
  }
) =>
  post(`${exam(examId)}/responses/${enc(responseId)}/remark`, {
    mark,
    rubric,
    guideRowId,
    outsideDecision,
    reason,
  });

export const skipRemark = (examId, responseId) =>
  post(`${exam(examId)}/responses/${enc(responseId)}/skip-remark`);

/**
 * POST …/responses/{rid}/retry-mark: a free re-run of one FAILED JeddAI mark
 * (03 §fallback, 10-int-findings) → 202 {runId, queued: 1}; 422 VALIDATION
 * {reason: MARK_NOT_FAILED} when that mark is no longer FAILED.
 */
export const retryMark = (examId, responseId) =>
  post(`${exam(examId)}/responses/${enc(responseId)}/retry-mark`);

/** PUT …/responses/{rid}/final {mark, rubric, reason}. */
export const setFinalMark = (examId, responseId, body) =>
  put(`${exam(examId)}/responses/${enc(responseId)}/final`, body);

/** DELETE …/responses/{rid}/final: Reset a manual final to the rule's value. */
export const resetFinalMark = (examId, responseId) =>
  del(`${exam(examId)}/responses/${enc(responseId)}/final`);

// --------------------------------------------- samples (§5.8)

export const getSamples = (examId) => get(`${exam(examId)}/samples`);

export const putSampleMark = (examId, questionId, responseId, body) =>
  put(`${exam(examId)}/samples/${enc(questionId)}/${enc(responseId)}`, body);

export const addSample = (examId, questionId) =>
  post(`${exam(examId)}/samples/${enc(questionId)}/add`);

export const removeSample = (examId, questionId, responseId) =>
  del(`${exam(examId)}/samples/${enc(questionId)}/${enc(responseId)}`);

export const skipSamples = (examId) => post(`${exam(examId)}/samples/skip`);

export const calibrate = (
  examId,
  { acknowledgeOrderWarnings = false } = {},
  idempotencyKey
) =>
  post(
    `${exam(examId)}/calibrate`,
    { acknowledgeOrderWarnings },
    { idempotencyKey: keyOr(idempotencyKey) }
  );

// ---------------------------------------------- feedback (§5.9)

export const getFeedbackList = (examId) => get(`${exam(examId)}/feedback`);

export const getScriptFeedback = (examId, scriptId) =>
  get(`${exam(examId)}/scripts/${enc(scriptId)}/feedback`);

/** PATCH …/responses/{rid}/feedback {overall, successCriteria}. */
export const patchResponseFeedback = (examId, responseId, body) =>
  patch(`${exam(examId)}/responses/${enc(responseId)}/feedback`, body);

/** POST …/responses/{rid}/comments {anchor, category, text, suggestion}. */
export const addComment = (examId, responseId, body) =>
  post(`${exam(examId)}/responses/${enc(responseId)}/comments`, body);

/** PATCH /exams/{id}/comments/{commentId} {text, suggestion} | {removed: true}. */
export const patchComment = (examId, commentId, body) =>
  patch(`${exam(examId)}/comments/${enc(commentId)}`, body);

/** POST /exams/{id}/feedback/approve {scriptIds | null (approve all)}. */
export const approveFeedback = (examId, { scriptIds = null } = {}) =>
  post(`${exam(examId)}/feedback/approve`, { scriptIds });

/** POST /exams/{id}/feedback/generate {overall, specific, criteria} → 202. */
export const generateFeedback = (
  examId,
  { overall = true, specific = true, criteria = false } = {},
  idempotencyKey
) =>
  post(
    `${exam(examId)}/feedback/generate`,
    { overall, specific, criteria },
    { idempotencyKey: keyOr(idempotencyKey) }
  );

export const retryFeedback = (examId, { responseIds = null } = {}) =>
  post(`${exam(examId)}/feedback/retry`, { responseIds });

// ---------------------------------------------- insights (§5.10)

/** GET /exams/{id}/insights?scope=TOTAL|<questionId>. */
export const getInsights = (examId, scope = 'TOTAL') =>
  get(`${exam(examId)}/insights${queryString({ scope })}`);

// ------------------------------- reports, exports, sharing (§5.11)

/** Report config → query: questions all|ids, include list, names SHOW|ANONYMOUS. */
const reportQuery = ({ questions = 'all', include, names = 'SHOW' } = {}) =>
  queryString({ questions, include, names });

export const getReportModel = (examId, config) =>
  get(`${exam(examId)}/report-model${reportQuery(config)}`);

/** POST /exams/{id}/report/send {config, recipients, message}. */
export const sendReport = (examId, body, idempotencyKey) =>
  post(`${exam(examId)}/report/send`, body, {
    idempotencyKey: keyOr(idempotencyKey),
  });

export const createReportLink = (examId, config) =>
  post(`${exam(examId)}/report/link`, { config });

export const listShareLinks = (examId, { cursor } = {}) =>
  get(`${exam(examId)}/links${queryString({ cursor })}`);

export const revokeShareLink = (examId, linkId) =>
  del(`${exam(examId)}/links/${enc(linkId)}`);

/** POST /exams/{id}/links/revoke-all {scope: REPORT|STUDENT_REPORT|null}. */
export const revokeAllLinks = (examId, scope = null) =>
  post(`${exam(examId)}/links/revoke-all`, { scope });

export const getRecentRecipients = () => get('/exams/recipients/recent');

/** GET /exams/{id}/exports/marks?includeCriteria=&names= (also Exams-home "Download marks"). */
export const exportMarks = (
  examId,
  { includeCriteria = false, names = 'SHOW' } = {}
) =>
  get(
    `${exam(examId)}/exports/marks${queryString({ includeCriteria, names })}`
  );

export const getStudentReportsModel = (examId, body) =>
  post(`${exam(examId)}/student-reports/model`, body);

/** GET /exams/{id}/student-emails → the saved {entries: [{scriptId, email}]}. */
export const getStudentEmails = (examId, { signal } = {}) =>
  get(`${exam(examId)}/student-emails`, { signal });

export const putStudentEmails = (examId, body) =>
  put(`${exam(examId)}/student-emails`, body);

export const matchStudentEmails = (examId, body) =>
  post(`${exam(examId)}/student-emails/match`, body);

export const sendStudentReports = (examId, body, idempotencyKey) =>
  post(`${exam(examId)}/student-reports/send`, body, {
    idempotencyKey: keyOr(idempotencyKey),
  });
